import React, { useState, useEffect, useRef } from 'react';
import { List, Menu, MenuItem, Button, Icon } from '@material-ui/core';
import useStyles from './ReportPhotos.styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import withInfiniteScroll from 'global/components/withInfiniteScroll';
import { get, set } from 'global/lib/lodash';
import { useSelector } from 'react-redux';
import { buildFilters } from 'global/utils/filter';
import { ImageCaption, PhotoRow } from './PhotoRow';
import ModalGateway from 'global/components/ModalGateway';
import { useResize } from 'global/hooks/useResize';
import { useScreen } from 'global/styles';
import { stopProp } from 'global/utils';
import withSearchBar from 'global/components/withSearchBar';
import { Header } from '../ReportList';

export default function ReportPhotos(props) {
  const filter = useSelector((state) => get(state, 'filter.reports.photo'));
  const realm = useSelector((state) => get(state, 'auth.realm.id'));
  const [imageModal, setImageModal] = useState(false);
  const container = useRef();
  const { isMobileXS } = useScreen();

  const [state, setState] = useState({
    photos: [],
    allPhotos: [],
    opts: [
      { title: 'All Photos', id: 'all' },
      { title: 'Highlights', id: 'highlights' },
      { title: 'Scores', id: 'scores' },
      { title: 'Tasks', id: 'tasks' },
    ],
    selectedOpt: 0,
    maxPageSize: 1000,
    pageSize: 28,
    columns: isMobileXS ? 2 : 4,
    anchor: null,
    selected: {},
  });

  useEffect(() => {
    setState((s) => ({ ...s, columns: isMobileXS ? 2 : 4 }));
  }, [isMobileXS]);

  const selectedOption = get(state, `opts.${state.selectedOpt}`, {});

  const filterProps = () =>
    buildFilters(filter, {
      includeRealm: true,
      convertName: {
        realm_type: 'tier',
        realm: 'realm_id',
        tier: 'realm_tier',
      },
      dontSort: true,
      dontFilter: ['ownership', 'searchstring'],
      overrideFilter: (_filt) => {
        const filt = { ..._filt };
        Object.keys(filt).map((k) => {
          if ((k !== 'quality_type' && filt[k] === 0) || (k === 'quality_type' && (filt[k] === 1 || selectedOption.id === 'highlights'))) {
            //highquality return all images but then filter the images returned
            delete filt[k];
          }
        });

        const { id } = selectedOption;
        if (id !== 'all' && id !== 'highlights') {
          const collName = id === 'scores' ? 'scorecard-score-images' : 'task-images';
          set(filt, 'collection_name', collName);
        }
        return filt;
      },
    });

  const [urlParams, setUrlParams] = useState({
    waitFor: null,
    useNamespace: false,
    updateV: 0,
    transformData: (d) => get(d, 'data', []).map((dd) => ({ ...dd, collectionName: get(dd, 'collectionName', get(dd, 'collection_name')) })),
    params: {
      page: { size: state.pageSize, number: 1 },
      ...filterProps(),
    },
  });
  const { data, meta, loading, error, clearData } = useRetrieveData(
    {
      url: get(selectedOption, 'id') === 'highlights' ? '/search/report-sections' : '/search/media',
    },
    urlParams
  );

  const changeOpt = (index) => {
    const opt = get(state, `opts.${index}`);

    if (opt.id === selectedOption.id) {
      return setState((s) => ({ ...s, anchor: null }));
    }

    clearData();
    set(urlParams, 'params.filter.collection_name', undefined);

    setState((s) => ({
      ...s,
      pageSize: s.pageSize,
      selectedOpt: index,
      photos: [],
      allPhotos: [],
      anchor: null,
    }));
  };

  useEffect(() => {
    setUrlParams((p) => ({
      ...p,
      updateV: p.updateV + 1,
      waitFor: undefined,
      useEffect: true,
      returnRaw: true,
      params: {
        ...p.params,
        ...{ page: { size: state.pageSize, number: 1 } },
        q: filter.searchstring,
        ...filterProps(),
      },
    }));
  }, [filter, realm, selectedOption]);

  const onImageClick = (img, index, e) => {
    setImageModal({ index });
  };

  const filterImages = (images = []) => {
    const { id } = selectedOption;

    const quality_type = get(filter, 'quality_type');
    if (id === 'highlights') {
      if (quality_type === 2) {
        //highquality
        return images.map((img) => {
          const { section_type } = img;
          if (section_type === 'Quality Issues') {
            img.images = img.images.filter((im) => im.collection_name === 'report-section-bp-images');
          }
          return img;
        });
      } else if (quality_type === 0) {
        //poor qual
        return images.map((img) => {
          const { section_type } = img;
          if (section_type === 'Quality Issues') {
            img.images = img.images.filter((im) => im.collection_name === 'report-section-site-images');
          }
          return img;
        });
      }
    }

    return images;
  };

  const currentPage = get(meta, 'page.current-page', 1);
  const lastPage = get(meta, 'page.last-page', null);
  const updatePage = (newPage) => {
    if (newPage <= lastPage) {
      setUrlParams({
        ...urlParams,
        params: {
          ...urlParams.params,
          page: { size: state.pageSize, number: newPage },
        },
      });
    }
  };

  useEffect(() => {
    if (loading) {
      setState((s) => ({ ...s, isLoading: true }));
    } else {
      let allPhotos = [...(currentPage <= 1 ? [] : state.allPhotos), ...data];

      let photos = filterImages(allPhotos).map((img, i) => ({
        ...img,
        canSelect: !!props.canSelect,
        onClick: (e) => onImageClick(img, i, e),
        selectImage,
      }));

      setState((s) => ({ ...s, photos, allPhotos, isLoading: false }));
    }
  }, [data, selectedOption, loading]);

  const classes = useStyles();

  const contSize = useResize(container.current);

  const closeContainer = () => {
    setState((s) => ({ ...s, anchor: null }));
  };

  const clickDrop = (e) => {
    stopProp(e);
    setState((s) => ({ ...s, anchor: e.currentTarget }));
  };

  const selectImage = (img) => {
    setState((s) => {
      const selected = { ...s.selected };
      const isSel = get(selected, img.id);
      set(selected, img.id, !!isSel ? undefined : img);

      props.onSelectedChange && props.onSelectedChange(selected);

      return {
        ...s,
        selected,
      };
    });
  };

  return (
    <div ref={container} onClick={closeContainer}>
      <Header {...props} />

      <ListWithPages
        handlePageChange={updatePage}
        currentPage={get(urlParams, 'params.page.number', 1)}
        lastPage={lastPage}
        data={state.photos}
        loading={loading || state.isLoading}
        error={error}
        selectedOption={selectedOption}
        COLUMNS={state.columns}
        contSize={contSize}
        alwaysReplaceData
        selected={state.selected}
        header={
          <>
            <Button className={classes.openMenu} onClick={clickDrop}>
              {get(state, `opts.${state.selectedOpt}.title`)}
              <div className="more">
                {`more`} <Icon>arrow_drop_down</Icon>
              </div>
            </Button>
            <Menu anchorEl={state.anchor} open={!!state.anchor}>
              {state.opts.map((opt, i) => {
                return (
                  <MenuItem key={i} selected={state.selectedOpt === i} onClick={() => changeOpt(i)}>
                    {opt.title}
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        }
        {...props}
      />

      <ModalGateway
        open={imageModal}
        onClose={() => setImageModal()}
        currentIndex={get(imageModal, 'index')}
        images={state.photos.map((x) => ({
          ...x,
          key: x.id,
          src: get(x, 'fullUrl'),
          caption: <ImageCaption {...x} />,
        }))}
      />
    </div>
  );
}

const ListWithPages = withSearchBar(withInfiniteScroll(PhotoRow, List));
